import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PopupState {
  id: string;
  onClose: () => void;
  closeOnDeselect?: boolean;
  closeOnOutsideClick?: boolean;
}

interface PopupManagerContextType {
  registerPopup: (popup: PopupState) => void;
  unregisterPopup: (id: string) => void;
  closeAllPopups: () => void;
  closePopupsOnDeselect: () => void;
  getActivePopups: () => PopupState[];
}

const PopupManagerContext = createContext<PopupManagerContextType | undefined>(undefined);

interface PopupManagerProviderProps {
  children: ReactNode;
}

export const PopupManagerProvider: React.FC<PopupManagerProviderProps> = ({ children }) => {
  const [popups, setPopups] = useState<Map<string, PopupState>>(new Map());

  const registerPopup = useCallback((popup: PopupState) => {
    setPopups(prev => new Map(prev).set(popup.id, popup));
  }, []);

  const unregisterPopup = useCallback((id: string) => {
    setPopups(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const closeAllPopups = useCallback(() => {
    setPopups(prev => {
      prev.forEach(popup => popup.onClose());
      return new Map();
    });
  }, []);

  const closePopupsOnDeselect = useCallback(() => {
    setPopups(prev => {
      const newMap = new Map();
      prev.forEach((popup, id) => {
        if (popup.closeOnDeselect !== false) { // Default to true
          popup.onClose();
        } else {
          newMap.set(id, popup);
        }
      });
      return newMap;
    });
  }, []);

  const getActivePopups = useCallback(() => {
    return Array.from(popups.values());
  }, [popups]);

  const value: PopupManagerContextType = {
    registerPopup,
    unregisterPopup,
    closeAllPopups,
    closePopupsOnDeselect,
    getActivePopups
  };

  return (
    <PopupManagerContext.Provider value={value}>
      {children}
    </PopupManagerContext.Provider>
  );
};

export const usePopupManager = (): PopupManagerContextType => {
  const context = useContext(PopupManagerContext);
  if (!context) {
    throw new Error('usePopupManager must be used within a PopupManagerProvider');
  }
  return context;
};
